/**
 * MstAdvancedOverrides — Registry / Feature / FeatureComponents / Shortcut
 * editor for the packaging wizard's MST configuration.
 *
 * The component fetches base-MSI rows for the selected table (via
 * GET /api/packages/{id}/msi-tables/{name}) and lets the operator queue
 * INSERT / UPDATE / DELETE operations that will be baked into alice.mst
 * on packaging.
 *
 * Only shown when MST mode is enabled on the wizard. Requires a
 * ``preparedPackageId`` — the base MSI must already exist on the
 * server (i.e. Prepare has been called).
 *
 * Edits save themselves shortly after each change. There used to be a
 * Save button, and "Continue with Packaging" did not press it for you, so
 * a queued delete that was never saved was silently left out of alice.mst.
 *
 * Through a ref it exposes:
 *  - `queueEdit(table, edit)`: the Auto-update suggestions panel queues a
 *    Registry or Feature change here, like a manual edit;
 *  - `flush()`: saves anything outstanding and resolves true once the
 *    server has it, false if the save failed. The wizard awaits this
 *    before it starts packaging.
 */
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Check, Loader2, Plus, RotateCw, Trash2, Undo2, X } from "lucide-react";

const AUTOSAVE_DELAY_MS = 600;

const TABLES = ["Registry", "Feature", "FeatureComponents", "Shortcut"];

// Columns shown by default per table. The Shortcut table is 16 columns
// wide (MSI 4.0 schema) and most of them are empty resource-DLL fields,
// so show the ones a packager needs to pick a row to delete; the rest
// are one toggle away.
const DEFAULT_COLUMNS = {
  Shortcut: ["Shortcut", "Directory_", "Name", "Target", "Description"],
};

const emptyEdits = () => Object.fromEntries(TABLES.map((t) => [t, []]));

const OP_STYLES = {
  insert: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  update: "bg-amber-500/20 text-amber-200 border-amber-500/40",
  delete: "bg-red-500/20 text-red-200 border-red-500/40",
};

const MstAdvancedOverrides = forwardRef(function MstAdvancedOverrides({
  apiBase,
  packageId,
  authHeaders,
  disabled = false,
}, ref) {
  const [activeTable, setActiveTable] = useState("Registry");
  const [baseData, setBaseData] = useState({});           // { tableName: { columns, rows, primary_keys } }
  const [loadingTable, setLoadingTable] = useState(false);
  const [edits, setEdits] = useState(emptyEdits);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);
  const [insertDraft, setInsertDraft] = useState(null);    // { columnValues: {...} } while adding
  const [showAllColumns, setShowAllColumns] = useState(false);

  // Auto-save plumbing. editsRef always holds the latest edits for the
  // save to send; dirtyRef marks changes the server doesn't have yet.
  const editsRef = useRef(edits);
  editsRef.current = edits;
  const dirtyRef = useRef(false);
  const timerRef = useRef(null);
  const inFlightRef = useRef(null);
  const saveRef = useRef(null);

  // Every user-driven change goes through here (the initial load of
  // persisted edits does not, so opening the panel never re-saves).
  const changeEdits = useCallback((updater) => {
    setEdits(updater);
    dirtyRef.current = true;
    setSavedAt(null);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => saveRef.current?.(), AUTOSAVE_DELAY_MS);
  }, []);

  // Load current persisted edits when the component mounts.
  // authHeaders is async in the wizard (getAuthHeaders); it was passed to
  // axios un-awaited, so these calls went out with a Promise for headers
  // and no bearer token. Awaiting a plain object is harmless.
  useEffect(() => {
    if (!packageId) return;
    (async () => {
      try {
        const r = await axios.get(`${apiBase}/packages/${packageId}/msi-table-edits`, {
          headers: await authHeaders(),
        });
        const persisted = r.data?.table_edits || {};
        setEdits(Object.fromEntries(TABLES.map((t) => [t, persisted[t] || []])));
      } catch {
        /* nothing persisted yet — fine */
      }
    })();
  }, [apiBase, packageId, authHeaders]);

  // Fetch base rows for the active table on demand.
  const loadTable = useCallback(
    async (tableName) => {
      if (baseData[tableName]) return; // cached
      setLoadingTable(true);
      setError(null);
      try {
        const r = await axios.get(
          `${apiBase}/packages/${packageId}/msi-tables/${tableName}`,
          { headers: await authHeaders() },
        );
        setBaseData((prev) => ({ ...prev, [tableName]: r.data }));
      } catch (e) {
        setError(e?.response?.data?.detail || e.message || "Failed to load table");
      } finally {
        setLoadingTable(false);
      }
    },
    [apiBase, packageId, authHeaders, baseData],
  );

  useEffect(() => {
    if (packageId) loadTable(activeTable);
  }, [activeTable, packageId, loadTable]);

  const meta = baseData[activeTable];
  const activeEdits = edits[activeTable] || [];
  const defaultColumns = DEFAULT_COLUMNS[activeTable];
  const shownColumns = !meta
    ? []
    : showAllColumns || !defaultColumns
    ? meta.columns
    : meta.columns.filter((c) => c.is_primary_key || defaultColumns.includes(c.name));
  const hiddenColumnCount = meta ? meta.columns.length - shownColumns.length : 0;

  const pkForRow = useCallback(
    (row, columns, primary_keys) => {
      return primary_keys.map((k) => row[k]).join("||");
    },
    [],
  );

  // Find the pending edit for a given base row (or new PK) so the UI
  // reflects "already queued". Match by primary-key composite string.
  const pendingByPk = useMemo(() => {
    if (!meta) return {};
    const out = {};
    activeEdits.forEach((e, i) => {
      const pk = pkForRow(e.row, meta.columns, meta.primary_keys);
      out[pk] = { edit: e, index: i };
    });
    return out;
  }, [activeEdits, meta, pkForRow]);

  const enqueueDelete = (row) => {
    if (!meta) return;
    // Build a row containing only PK columns.
    const pkOnly = {};
    meta.primary_keys.forEach((k) => (pkOnly[k] = row[k]));
    const pk = pkForRow(pkOnly, meta.columns, meta.primary_keys);
    const existing = pendingByPk[pk];
    const next = [...activeEdits];
    if (existing) next.splice(existing.index, 1);
    next.push({ op: "delete", row: pkOnly });
    changeEdits((prev) => ({ ...prev, [activeTable]: next }));
  };

  const enqueueUpdate = (row, columnName, newValue) => {
    if (!meta) return;
    const pkOnly = {};
    meta.primary_keys.forEach((k) => (pkOnly[k] = row[k]));
    const pk = pkForRow(pkOnly, meta.columns, meta.primary_keys);
    const existing = pendingByPk[pk];
    const next = [...activeEdits];
    let mergedRow = { ...pkOnly, [columnName]: newValue };
    if (existing && existing.edit.op === "update") {
      mergedRow = { ...existing.edit.row, ...mergedRow };
      next.splice(existing.index, 1);
    } else if (existing) {
      next.splice(existing.index, 1);
    }
    next.push({ op: "update", row: mergedRow });
    changeEdits((prev) => ({ ...prev, [activeTable]: next }));
  };

  const removeEditAt = (index) => {
    const next = [...activeEdits];
    next.splice(index, 1);
    changeEdits((prev) => ({ ...prev, [activeTable]: next }));
  };

  const commitInsert = () => {
    if (!insertDraft || !meta) return;
    // Basic PK presence check
    for (const k of meta.primary_keys) {
      if (!insertDraft.columnValues[k]) {
        setError(`Primary-key column '${k}' is required`);
        return;
      }
    }
    changeEdits((prev) => ({
      ...prev,
      [activeTable]: [
        ...(prev[activeTable] || []),
        { op: "insert", row: { ...insertDraft.columnValues } },
      ],
    }));
    setInsertDraft(null);
    setError(null);
  };

  // Resolves true once the server holds the current edits. Saves run one
  // after another so an older request can never land after a newer one.
  const save = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    const previous = inFlightRef.current || Promise.resolve(true);
    const run = previous.then(async () => {
      dirtyRef.current = false;
      setSaving(true);
      setError(null);
      try {
        const r = await axios.put(
          `${apiBase}/packages/${packageId}/msi-table-edits`,
          {
            // The wizard commits property overrides separately; here we
            // only persist table_edits so we don't clobber them.
            property_overrides: undefined,
            table_edits: Object.fromEntries(TABLES.map((t) => [t, editsRef.current[t] || []])),
          },
          { headers: await authHeaders() },
        );
        if (r.data?.rewrite?.mst_error) {
          setError(`Saved, but MST rewrite reported: ${r.data.rewrite.mst_error}`);
        } else {
          setSavedAt(new Date());
        }
        return true;
      } catch (e) {
        dirtyRef.current = true;
        setError(e?.response?.data?.detail || e.message || "Save failed");
        return false;
      } finally {
        setSaving(false);
      }
    });
    inFlightRef.current = run;
    return run;
  }, [apiBase, packageId, authHeaders]);
  saveRef.current = save;

  const totalPending = TABLES.reduce((n, t) => n + (edits[t]?.length || 0), 0);

  // Queue an edit from outside (Auto-update suggestions). Replaces any
  // pending edit for the same primary key, the same as a manual edit does,
  // and switches to that table so the packager sees what was queued.
  useImperativeHandle(ref, () => ({
    queueEdit(table, edit) {
      if (!TABLES.includes(table)) return;
      const keys = Object.keys(edit.keyRow || edit.row);
      const keyOf = (row) => JSON.stringify(keys.map((k) => row[k]));
      const target = keyOf(edit.row);
      changeEdits((prev) => {
        const pending = (prev[table] || []).filter((e) => keyOf(e.row) !== target);
        return { ...prev, [table]: [...pending, { op: edit.op, row: edit.row }] };
      });
      setActiveTable(table);
    },
    flush() {
      if (timerRef.current || dirtyRef.current) return saveRef.current();
      return inFlightRef.current || Promise.resolve(true);
    },
  }), [changeEdits]);

  // Leaving MST mode unmounts this panel; don't drop a pending save.
  useEffect(() => () => {
    if (timerRef.current || dirtyRef.current) saveRef.current?.();
  }, []);

  if (!packageId) {
    return (
      <div className="text-xs text-slate-400 italic">
        Run <span className="text-slate-200 font-medium">Prepare</span> first — advanced
        overrides need the base MSI on the server.
      </div>
    );
  }

  return (
    <div className="space-y-3 min-w-0" data-testid="mst-advanced-overrides">
      {/* Tab picker */}
      <div className="flex gap-1 border-b border-slate-700">
        {TABLES.map((t) => {
          const pending = (edits[t] || []).length;
          const active = t === activeTable;
          return (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTable(t)}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                active
                  ? "border-cyan-400 text-cyan-200"
                  : "border-transparent text-slate-400 hover:text-slate-200"
              }`}
              data-testid={`mst-tab-${t.toLowerCase()}`}
            >
              {t}
              {pending > 0 && (
                <span className="ml-1.5 rounded bg-cyan-500/20 text-cyan-200 text-[10px] px-1.5 py-0.5">
                  {pending}
                </span>
              )}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setInsertDraft({ columnValues: {} })}
            disabled={disabled || !meta || loadingTable}
            className="px-2 py-1 text-xs rounded bg-emerald-600/80 hover:bg-emerald-600 text-white flex items-center gap-1 disabled:opacity-50"
            data-testid={`mst-insert-row-${activeTable.toLowerCase()}`}
          >
            <Plus className="h-3 w-3" /> Insert row
          </button>
          {/* Save status: edits save themselves; Retry only after a failure. */}
          {saving ? (
            <span className="text-[11px] text-slate-400 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Saving…
            </span>
          ) : error && dirtyRef.current ? (
            <button
              type="button"
              onClick={save}
              disabled={disabled}
              className="px-2 py-1 text-xs rounded bg-red-600/80 hover:bg-red-600 text-white flex items-center gap-1 disabled:opacity-50"
              data-testid="mst-save-edits"
            >
              <RotateCw className="h-3 w-3" /> Retry save
            </button>
          ) : savedAt ? (
            <span className="text-[11px] text-emerald-400 flex items-center gap-1" data-testid="mst-saved">
              <Check className="h-3 w-3" /> Saved
            </span>
          ) : null}
        </div>
      </div>

      {savedAt && (
        <div className="text-[11px] text-emerald-400">
          Saved {savedAt.toLocaleTimeString()}. These edits go into alice.mst when the package is built.
        </div>
      )}
      {error && (
        <div className="text-[11px] text-red-400" data-testid="mst-overrides-error">{error}</div>
      )}

      {activeTable === "Shortcut" && (
        <p className="text-[11px] text-slate-400">
          Delete a row to stop Windows Installer creating that shortcut. Deleting it here replaces the
          post-install clean-up in the Shortcuts pane, so you don&apos;t need to tick the same shortcut in both.
        </p>
      )}
      {meta?.exists_in_base && (hiddenColumnCount > 0 || showAllColumns) && defaultColumns && (
        <button
          type="button"
          onClick={() => setShowAllColumns((v) => !v)}
          className="text-[11px] text-cyan-300 hover:text-cyan-200 underline-offset-2 hover:underline"
          data-testid="mst-toggle-all-columns"
        >
          {showAllColumns ? "Show key columns only" : `Show all ${meta.columns.length} columns (${hiddenColumnCount} hidden)`}
        </button>
      )}

      {/* Pending-edits chip strip */}
      {activeEdits.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {activeEdits.map((e, i) => {
            const label =
              e.op === "delete"
                ? `DEL ${Object.values(e.row).join(", ")}`
                : e.op === "insert"
                ? `NEW ${meta?.primary_keys.map((k) => e.row[k]).join(", ") || ""}`
                : `SET ${meta?.primary_keys.map((k) => e.row[k]).join(", ") || ""}`;
            return (
              <span
                key={i}
                className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${OP_STYLES[e.op]}`}
              >
                {label}
                <button
                  type="button"
                  onClick={() => removeEditAt(i)}
                  className="hover:text-white/80"
                  data-testid={`mst-remove-edit-${i}`}
                  aria-label="Undo this edit"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* Base rows viewer / editor */}
      {loadingTable && (
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading {activeTable} table…
        </div>
      )}

      {meta && !loadingTable && (
        <div className="border border-slate-700 rounded overflow-x-auto max-h-72 overflow-y-auto">
          {!meta.exists_in_base && (
            <div className="p-3 text-xs text-slate-400 italic">
              Base MSI does not include a {activeTable} table. You can still Insert new rows.
            </div>
          )}
          {meta.exists_in_base && (
            <table className="w-full text-xs">
              <thead className="bg-slate-800 sticky top-0">
                <tr>
                  {shownColumns.map((c) => (
                    <th
                      key={c.name}
                      className={`px-2 py-1 text-left font-medium ${c.is_primary_key ? "text-cyan-300" : "text-slate-300"}`}
                      title={c.type_code}
                    >
                      {c.name}
                    </th>
                  ))}
                  <th className="px-2 py-1 sticky right-0 bg-slate-800"></th>
                </tr>
              </thead>
              <tbody>
                {meta.rows.map((row, ri) => {
                  const pkOnly = {};
                  meta.primary_keys.forEach((k) => (pkOnly[k] = row[k]));
                  const pk = pkForRow(pkOnly, meta.columns, meta.primary_keys);
                  const pending = pendingByPk[pk];
                  const isDeleted = pending?.edit?.op === "delete";
                  return (
                    <tr
                      key={pk || ri}
                      className={`odd:bg-slate-900 even:bg-slate-850 ${isDeleted ? "opacity-40 line-through" : ""}`}
                    >
                      {shownColumns.map((c) => {
                        const pendingOverride =
                          pending?.edit?.op === "update" && pending.edit.row[c.name] !== undefined
                            ? pending.edit.row[c.name]
                            : undefined;
                        const displayValue =
                          pendingOverride !== undefined ? pendingOverride : row[c.name] ?? "";
                        return (
                          <td key={c.name} className="px-2 py-1 text-slate-300 align-top">
                            {c.is_primary_key ? (
                              <span className="font-mono whitespace-nowrap">{displayValue}</span>
                            ) : (
                              <input
                                type="text"
                                defaultValue={displayValue}
                                disabled={disabled || isDeleted}
                                onBlur={(ev) => {
                                  const nv = ev.target.value;
                                  if (nv !== (row[c.name] ?? "")) {
                                    enqueueUpdate(row, c.name, nv);
                                  }
                                }}
                                title={displayValue}
                                className={`w-full min-w-[9rem] bg-transparent border-b border-slate-700 focus:border-cyan-500 outline-none px-1 py-0.5 ${
                                  pendingOverride !== undefined ? "text-amber-200" : ""
                                }`}
                                data-testid={`mst-edit-${activeTable.toLowerCase()}-${ri}-${c.name}`}
                              />
                            )}
                          </td>
                        );
                      })}
                      {/* Pinned right so delete stays reachable however wide the table is. */}
                      <td className="px-2 py-1 sticky right-0 bg-slate-900 align-top">
                        {isDeleted ? (
                          <button
                            type="button"
                            onClick={() => removeEditAt(pending.index)}
                            className="text-slate-400 hover:text-slate-200"
                            title="Undo delete"
                          >
                            <Undo2 className="h-3 w-3" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => enqueueDelete(row)}
                            disabled={disabled}
                            className="text-red-400 hover:text-red-300 disabled:opacity-50"
                            title="Queue delete"
                            data-testid={`mst-delete-row-${activeTable.toLowerCase()}-${ri}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Insert-row modal */}
      {insertDraft && meta && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60">
          <div className="alice-popup bg-slate-900 border border-slate-700 rounded-lg p-5 w-full max-w-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-slate-200">
                Insert row into {activeTable}
              </h3>
              <button
                type="button"
                onClick={() => setInsertDraft(null)}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              {meta.columns.map((c) => (
                <div key={c.name} className="flex items-center gap-2">
                  <label
                    className={`text-xs w-32 shrink-0 ${c.is_primary_key ? "text-cyan-300 font-medium" : "text-slate-300"}`}
                    title={c.type_code}
                  >
                    {c.name}
                    {c.is_primary_key && <span className="text-red-400 ml-0.5">*</span>}
                  </label>
                  <input
                    type="text"
                    placeholder={c.type_code}
                    className="flex-1 bg-slate-800 border border-slate-700 focus:border-cyan-500 rounded px-2 py-1 text-xs text-slate-200 outline-none"
                    onChange={(ev) =>
                      setInsertDraft((prev) => ({
                        ...prev,
                        columnValues: { ...prev.columnValues, [c.name]: ev.target.value },
                      }))
                    }
                    data-testid={`mst-insert-input-${c.name}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setInsertDraft(null)}
                className="px-3 py-1 text-xs rounded bg-slate-700 hover:bg-slate-600 text-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={commitInsert}
                className="px-3 py-1 text-xs rounded bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="mst-insert-confirm"
              >
                Queue insert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default MstAdvancedOverrides;
